import { useState, useEffect } from 'react';
import { HostProvider } from './HostProvider';
import { useSession } from './auth/useSession';
import { LoginScreen } from './auth/LoginScreen';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { Dashboard } from './routes/Dashboard';
import { Console } from './routes/Console';
import { ModuleHost } from './routes/ModuleHost';
import { MODULE_REGISTRY } from './routes/module-registry';
import { ConfirmModalRoot } from './components/ConfirmModal';
import { ToastRoot } from './components/Toast';
import { usePathname, withAppBasePath } from './nav';

type ModuleKey = keyof typeof MODULE_REGISTRY;

const ALL_MODULES = Object.keys(MODULE_REGISTRY) as ModuleKey[];

// localStorage key for surviving full-page reloads
const LAST_URL_STORAGE_KEY = 'admin_portal_last_url_per_module_v1';

function loadLastUrls(): Partial<Record<ModuleKey, string>> {
  try {
    const raw = localStorage.getItem(LAST_URL_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch { /* ignore */ }
  return {};
}

function saveLastUrls(urls: Partial<Record<ModuleKey, string>>) {
  try {
    localStorage.setItem(LAST_URL_STORAGE_KEY, JSON.stringify(urls));
  } catch { /* quota or disabled — ignore */ }
}

export function App() {
  // ── ALL hooks at the top, before ANY early return (rules-of-hooks) ──
  const session = useSession();
  const pathname = usePathname();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Last URL the user was on inside each module — restored when they
  // click the sidebar tile to come back. Survives page reload via
  // localStorage so a hard-refresh also resumes where you left off.
  const [lastUrl, setLastUrl] = useState<Partial<Record<ModuleKey, string>>>(
    () => loadLastUrls()
  );

  // Compute activeModule from pathname (no hooks — just a derived value)
  const moduleMatch = pathname.match(/^\/m\/([a-z-]+)(?:\/|$)/);
  const activeModule: ModuleKey | null =
    moduleMatch && moduleMatch[1] in MODULE_REGISTRY
      ? (moduleMatch[1] as ModuleKey)
      : null;

  // Remote console route: /chat or /chat/{room}. Not a module (/m/…).
  const consoleMatch = pathname.match(/^\/chat(?:\/([a-z-]+))?/);
  const consoleRoom: string | null = consoleMatch
    ? ['web', 'order'].includes(consoleMatch[1] ?? '')
      ? consoleMatch[1]!
      : 'web'
    : null;

  // Effect: close drawer on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  // Effect: close drawer on Escape
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen]);

  // Effect: track lastUrl per module — captures BOTH host nav (sidebar
  // click → /m/{key}) AND intra-module nav (clicking a screen inside a
  // module). The patched window.history.pushState in nav.ts ensures
  // module-internal navigation triggers usePathname updates.
  useEffect(() => {
    if (activeModule && pathname.startsWith(`/m/${activeModule}`)) {
      setLastUrl((prev) => {
        if (prev[activeModule] === pathname) return prev;
        const next = { ...prev, [activeModule]: pathname };
        saveLastUrls(next);
        return next;
      });
    }
  }, [pathname, activeModule]);

  // ── Early returns AFTER all hooks ──
  if (session.state === 'loading') return <div className="loading">Loading…</div>;
  if (session.state === 'anonymous') return <LoginScreen />;
  if (session.state === 'not-admin') return <NoAccessScreen lineUserId={session.lineUserId} />;

  // ── Render ──
  return (
    <HostProvider session={session}>
      <div className={`layout${sidebarOpen ? ' sidebar-open' : ''}`}>
        <Sidebar pathname={pathname} lastUrlByModule={lastUrl} />
        {sidebarOpen && <div className="sidebar-scrim" onClick={() => setSidebarOpen(false)} />}
        <div className="main">
          <Topbar onToggleSidebar={() => setSidebarOpen((v) => !v)} />
          <div className="content">
            {consoleRoom ? (
              <Console room={consoleRoom} />
            ) : (
              !activeModule && <Dashboard />
            )}

            {/*
              ALL modules eager-mount on first admin render and stay
              mounted for the whole session. The active one is visible
              (display:block); the others are hidden (display:none) but
              still:
                • holding their SSE connection open
                • running their useEffect timers
                • fetching/refreshing data when events arrive
                • preserving scroll position, open dialogs, sub-tabs

              Switching is purely a CSS visibility flip — no remount,
              no re-download of the lazy chunk, no re-fetch of initial
              data. Every module is ALWAYS doing its job, regardless of
              which tile you're looking at.

              Trade-off: initial sign-in pays the cost of 6 parallel
              lazy-chunk downloads + 6 initial fetches + 6 SSE handshakes.
              On broadband desktop this is sub-second. On slow cellular
              it might add ~2s to first paint after auth — acceptable
              for an operator portal.
            */}
            {ALL_MODULES.map((key) => (
              <div
                key={key}
                style={{
                  display: key === activeModule ? 'block' : 'none',
                  height: '100%',
                }}
              >
                <ModuleHost moduleKey={key} />
              </div>
            ))}
          </div>
        </div>
        <ConfirmModalRoot />
        <ToastRoot />
      </div>
    </HostProvider>
  );
}

function NoAccessScreen({ lineUserId }: { lineUserId: string }) {
  return (
    <div className="no-access">
      <h1>Not authorized</h1>
      <p>Your LINE id <code>{lineUserId}</code> isn't an admin yet.</p>
      <p>Ask an existing admin to promote you, or add your id to the <code>WEB_BOOTSTRAP_ADMINS</code> env var on Vercel + Fly.</p>
      <p>See <code>README.md</code> → "First-admin bootstrap".</p>
      <a href={`/api/auth/line/logout?next=${encodeURIComponent(withAppBasePath('/'))}`}>Sign out</a>
    </div>
  );
}
